import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'

import type { TaskFeedPlatform } from '@/app/[locale]/tasks/components/TaskFeedCard/taskFeedCard.constants'
import {
  collectFeedPlatforms,
  toFeedPlatformsParam
} from '@/app/[locale]/tasks/components/TasksFeedNav/feedPlatforms'
import {
  MY_FEED_TYPES,
  type TasksFeedSortId,
  toFeedSortParam
} from '@/app/[locale]/tasks/components/TasksFeedNav/tasksFeedNav.constants'
import type { TaskCardDemoRow } from '@/app/[locale]/tasks/components/TasksTaskCards/tasksTaskCards.constants'
import { useFanTasksAccess } from '@/features/tasks/hooks/useFanTasksAccess'
import type { TasksMyStatus } from '@/lib/tasks/tasksTabs'

import {
  getTaskGroups,
  taskGroupsAsProgressPage,
  type WithTaskGroup
} from '../api/taskGroupsApi'
import {
  type ListTaskProgressesResponse,
  type MyActiveTaskProgress,
  type TaskCardStatusParam,
  type TaskFeedFilterParams,
  type TaskListTypeFilter
} from '../api/watchTaskApi'
import { mapTaskProgressToTaskRow } from '../mappers/mapTaskProgressToTaskRow'
import { TASKS_FAN_LIST_FIRST_PAGE } from '../tasks.constants'
import {
  resolveTaskGroupStatus,
  type TaskGroupStatusResult
} from '../utils/taskGroupStatus'
import { useStickyFeedPlatforms } from './useStickyFeedPlatforms'

const ACTIVE_PAGE_SIZE = 50
const CLOSED_PAGE_SIZE = 24

export type MyTasksFeedItem = {
  key: string
  row: TaskCardDemoRow
  /** One status for all the platforms of the card's group. */
  groupStatus?: TaskGroupStatusResult
  list: 'active' | 'closed'
}

function toItems(
  progresses: MyActiveTaskProgress[],
  list: 'active' | 'closed'
): MyTasksFeedItem[] {
  return progresses.map(progress => {
    const row = mapTaskProgressToTaskRow(progress, { listVariant: list })
    const group = (progress as MyActiveTaskProgress & WithTaskGroup).task_group
    return {
      key: `${list}-${progress.task_progress_id ?? progress.id ?? row.id}`,
      row,
      groupStatus: (group && resolveTaskGroupStatus(group)) ?? undefined,
      list
    }
  })
}

/** Next page while the pages loaded so far are short of the total. */
export function getNextTaskProgressPage(
  pageSize: number
): (
  lastPage: ListTaskProgressesResponse,
  allPages: ListTaskProgressesResponse[],
  lastPageParam: number
) => number | undefined {
  return (lastPage, allPages, lastPageParam) => {
    if (lastPage.task_progresses.length < pageSize) {
      return undefined
    }
    const loaded = allPages.reduce((n, p) => n + p.task_progresses.length, 0)
    return loaded >= lastPage.total ? undefined : lastPageParam + 1
  }
}

const CARD_STATUS_PARAM: Partial<Record<TasksMyStatus, TaskCardStatusParam>> = {
  actionRequired: 'action_required',
  frozen: 'frozen',
  inProgress: 'in_progress'
}

export type MyTasksFeedFilters = {
  status: TasksMyStatus
  sort: TasksFeedSortId
  platforms: readonly TaskFeedPlatform[]
  /** Any-of; empty = every type My tasks lists. */
  types: readonly TaskListTypeFilter[]
}

/** One page of the My list (cards in work or with a retry on offer). */
async function fetchMyPage(
  page: number,
  pageSize: number,
  types: readonly TaskListTypeFilter[],
  filter: TaskFeedFilterParams
): Promise<ListTaskProgressesResponse> {
  const typeFilter =
    types.length > 0
      ? MY_FEED_TYPES.filter(type => types.includes(type))
      : MY_FEED_TYPES
  // Only watch picked: My never lists it.
  if (typeFilter.length === 0) {
    return { task_progresses: [], total: 0, available_platforms: [] }
  }
  return taskGroupsAsProgressPage(
    await getTaskGroups('my', {
      page,
      page_size: pageSize,
      types: typeFilter,
      ...filter
    })
  )
}

async function fetchClosedPage(
  page: number,
  pageSize: number,
  types: readonly TaskListTypeFilter[],
  filter: TaskFeedFilterParams
): Promise<ListTaskProgressesResponse> {
  return taskGroupsAsProgressPage(
    await getTaskGroups('closed', {
      page,
      page_size: pageSize,
      types: types.length > 0 ? types : undefined,
      ...filter
    })
  )
}

/** How many cards a My tab would list for these types and platforms. */
export async function countMyTasks(
  status: TasksMyStatus,
  types: readonly TaskListTypeFilter[],
  platforms: readonly TaskFeedPlatform[]
): Promise<number> {
  const filter = { platforms: toFeedPlatformsParam(platforms) }
  const [active, closed] = await Promise.all([
    status === 'ended'
      ? null
      : fetchMyPage(TASKS_FAN_LIST_FIRST_PAGE, 1, types, {
          ...filter,
          card_status: CARD_STATUS_PARAM[status]
        }),
    status === 'all' || status === 'ended'
      ? fetchClosedPage(TASKS_FAN_LIST_FIRST_PAGE, 1, types, filter)
      : null
  ])
  return (active?.total ?? 0) + (closed?.total ?? 0)
}

/** Cards that need the fan, whatever the feed's filters: the tab's counter. */
export function useMyActionRequiredCount(enabled: boolean): number {
  const { canParticipateInFanTasks } = useFanTasksAccess()
  const { data } = useQuery({
    queryKey: ['tasks', 'progress', 'active', 'actionRequiredCount'],
    queryFn: () =>
      fetchMyPage(TASKS_FAN_LIST_FIRST_PAGE, 1, [], {
        card_status: 'action_required'
      }),
    enabled: enabled && canParticipateInFanTasks
  })
  return data?.total ?? 0
}

export function useMyTasksFeed(
  { status, sort, platforms: picked, types }: MyTasksFeedFilters,
  enabled: boolean
) {
  const { canParticipateInFanTasks } = useFanTasksAccess()
  const canLoad = enabled && canParticipateInFanTasks
  const showActive = status !== 'ended'
  const showClosed = status === 'all' || status === 'ended'
  // Platform, type, card status and order are applied by the API over every page.
  const feedFilter: TaskFeedFilterParams = {
    platforms: toFeedPlatformsParam(picked),
    sort: toFeedSortParam(sort)
  }
  const activeFilter: TaskFeedFilterParams = {
    ...feedFilter,
    card_status: CARD_STATUS_PARAM[status]
  }
  const activeQuery = useInfiniteQuery({
    queryKey: ['tasks', 'progress', 'active', activeFilter, types],
    initialPageParam: TASKS_FAN_LIST_FIRST_PAGE,
    queryFn: ({ pageParam }) =>
      fetchMyPage(pageParam, ACTIVE_PAGE_SIZE, types, activeFilter),
    getNextPageParam: getNextTaskProgressPage(ACTIVE_PAGE_SIZE),
    enabled: canLoad && showActive
  })

  const closedQuery = useInfiniteQuery({
    queryKey: ['tasks', 'progress', 'closed', 'feed', feedFilter, types],
    initialPageParam: TASKS_FAN_LIST_FIRST_PAGE,
    queryFn: ({ pageParam }) =>
      fetchClosedPage(pageParam, CLOSED_PAGE_SIZE, types, feedFilter),
    getNextPageParam: getNextTaskProgressPage(CLOSED_PAGE_SIZE),
    enabled: canLoad && showClosed
  })

  const activeItems = useMemo(
    () =>
      toItems(
        activeQuery.data?.pages.flatMap(page => page.task_progresses) ?? [],
        'active'
      ),
    [activeQuery.data]
  )

  const closedItems = useMemo(
    () =>
      toItems(
        closedQuery.data?.pages.flatMap(page => page.task_progresses) ?? [],
        'closed'
      ),
    [closedQuery.data]
  )

  const items = useMemo(
    () => [
      ...(showActive ? activeItems : []),
      ...(showClosed ? closedItems : [])
    ],
    [activeItems, closedItems, showActive, showClosed]
  )

  const queries = [
    ...(showActive ? [activeQuery] : []),
    ...(showClosed ? [closedQuery] : [])
  ]
  // No fan session on the server render: stay on the skeleton until the
  // client knows who is looking (guests are sent back to Explore).
  const isLoading = !canLoad || queries.some(q => !q.isFetched)
  const isError = queries.length > 0 && queries.every(q => q.isError)

  // The Platforms menu: every platform of the lists this chip shows
  // (platform filter aside), known once they answered.
  const activePages = activeQuery.data?.pages
  const closedPages = closedQuery.data?.pages
  const fetchedPlatforms = useMemo(
    () =>
      isLoading
        ? undefined
        : collectFeedPlatforms([
            showActive ? activePages?.at(-1)?.available_platforms : undefined,
            showClosed ? closedPages?.at(-1)?.available_platforms : undefined
          ]),
    [activePages, closedPages, isLoading, showActive, showClosed]
  )
  const platforms = useStickyFeedPlatforms(fetchedPlatforms)

  const {
    hasNextPage: activeHasNextPage,
    isFetchingNextPage: activeIsFetchingNextPage,
    fetchNextPage: fetchActiveNextPage,
    refetch: refetchActive
  } = activeQuery
  const {
    hasNextPage: closedHasNextPage,
    isFetchingNextPage: closedIsFetchingNextPage,
    fetchNextPage: fetchClosedNextPage,
    refetch: refetchClosed
  } = closedQuery

  const activeHasMore = showActive && Boolean(activeHasNextPage)
  const closedHasMore = showClosed && Boolean(closedHasNextPage)

  // Closed tasks follow the active ones, so the active list is drained first.
  const loadMore = useCallback(() => {
    if (activeHasMore) {
      if (!activeIsFetchingNextPage) {
        void fetchActiveNextPage()
      }
      return
    }
    if (closedHasMore && !closedIsFetchingNextPage) {
      void fetchClosedNextPage()
    }
  }, [
    activeHasMore,
    activeIsFetchingNextPage,
    closedHasMore,
    closedIsFetchingNextPage,
    fetchActiveNextPage,
    fetchClosedNextPage
  ])

  const refetch = useCallback(async () => {
    await Promise.all([
      showActive ? refetchActive() : null,
      showClosed ? refetchClosed() : null
    ])
  }, [refetchActive, refetchClosed, showActive, showClosed])

  return {
    items,
    platforms,
    /** The platforms menu reflects the current lists (not a stale one). */
    platformsFetched: !isLoading,
    isLoading,
    isError,
    hasMore: activeHasMore || closedHasMore,
    isLoadingMore: activeIsFetchingNextPage || closedIsFetchingNextPage,
    loadMore,
    refetch
  }
}
